/**
 * Sélecteur de période avec presets
 */

import { useState } from 'react';
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  subMonths,
} from 'date-fns';
import { fr } from 'date-fns/locale';

export interface DateRange {
  from: Date;
  to: Date;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

type Preset = 'today' | 'yesterday' | 'week' | 'month' | 'last_month' | 'year' | 'custom';

const presets: { key: Preset; label: string }[] = [
  { key: 'today', label: 'Aujourd\'hui' },
  { key: 'yesterday', label: 'Hier' },
  { key: 'week', label: 'Cette semaine' },
  { key: 'month', label: 'Ce mois' },
  { key: 'last_month', label: 'Mois dernier' },
  { key: 'year', label: 'Cette année' },
  { key: 'custom', label: 'Personnalisé' },
];

export default function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const [activePreset, setActivePreset] = useState<Preset>('month');
  const [showCustom, setShowCustom] = useState(false);

  const applyPreset = (preset: Preset) => {
    const now = new Date();
    let from: Date;
    let to: Date;

    switch (preset) {
      case 'today':
        from = startOfDay(now);
        to = endOfDay(now);
        break;
      case 'yesterday':
        from = startOfDay(subDays(now, 1));
        to = endOfDay(subDays(now, 1));
        break;
      case 'week':
        from = startOfWeek(now, { weekStartsOn: 1 });
        to = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'month':
        from = startOfMonth(now);
        to = endOfMonth(now);
        break;
      case 'last_month':
        from = startOfMonth(subMonths(now, 1));
        to = endOfMonth(subMonths(now, 1));
        break;
      case 'year':
        from = startOfYear(now);
        to = endOfYear(now);
        break;
      case 'custom':
        setShowCustom(true);
        setActivePreset('custom');
        return;
      default:
        return;
    }

    setActivePreset(preset);
    setShowCustom(false);
    onChange({ from, to });
  };

  const handleCustomDateChange = (field: 'from' | 'to', dateStr: string) => {
    const date = new Date(dateStr);
    if (field === 'from') {
      onChange({ ...value, from: startOfDay(date) });
    } else {
      onChange({ ...value, to: endOfDay(date) });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex flex-wrap gap-2 mb-3">
        {presets.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => applyPreset(key)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activePreset === key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {showCustom && (
        <div className="flex items-center gap-3 pt-3 border-t">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Du</label>
            <input
              type="date"
              value={format(value.from, 'yyyy-MM-dd')}
              onChange={(e) => handleCustomDateChange('from', e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Au</label>
            <input
              type="date"
              value={format(value.to, 'yyyy-MM-dd')}
              onChange={(e) => handleCustomDateChange('to', e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>
      )}

      <div className="mt-2 text-sm text-gray-500">
        Période : {format(value.from, 'dd MMM yyyy', { locale: fr })} -{' '}
        {format(value.to, 'dd MMM yyyy', { locale: fr })}
      </div>
    </div>
  );
}
